import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { Card, CardTitle, Field, Label, Input, Button, Text } from '@/components/ui'
import { useTranslations } from '@/lib/i18n'
import { jsonCsrfHeaders } from '@/lib/csrf'

interface TestResult {
  ok: boolean
  message: string
}

/**
 * Manage a merchant's payment gateway credentials: paste the API + OUTGOING
 * keys, test them live against the gateway (without saving), then save. Stored
 * encrypted server-side.
 */
export function CredentialForm({ merchantId, configured }: { merchantId: number; configured: boolean }) {
  const { t } = useTranslations()
  const form = useForm({ api_key: '', outgoing_key: '' })
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState<TestResult | null>(null)

  function save(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ gateway_credential: data }))
    form.patch(`/merchants/${merchantId}/credential`)
  }

  async function testConnection() {
    setTesting(true)
    setResult(null)
    try {
      const response = await fetch(`/merchants/${merchantId}/credential/test_connection`, {
        method: 'POST',
        headers: jsonCsrfHeaders(),
        body: JSON.stringify({ api_key: form.data.api_key, outgoing_key: form.data.outgoing_key }),
      })
      setResult(await response.json())
    } catch {
      setResult({ ok: false, message: t('credentials.test.failed') })
    } finally {
      setTesting(false)
    }
  }

  return (
    <Card className="mt-6">
      <CardTitle>{t('credentials.title')}</CardTitle>
      <Text className="mt-1">{t('credentials.description')}</Text>
      {configured && (
        <p className="mt-2 inline-flex items-center gap-1.5 font-medium text-status-success-fg">
          <CheckCircleIcon aria-hidden="true" className="size-5" />
          {t('merchants.configured')}
        </p>
      )}

      <form onSubmit={save} className="mt-4 flex flex-col gap-4">
        <Field>
          <Label>{t('credentials.api_key')}</Label>
          <Input
            value={form.data.api_key}
            onChange={(e) => form.setData('api_key', e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field>
          <Label>{t('credentials.outgoing_key')}</Label>
          <Input
            type="password"
            value={form.data.outgoing_key}
            onChange={(e) => form.setData('outgoing_key', e.target.value)}
            autoComplete="off"
          />
        </Field>

        {result && (
          <Text className={result.ok ? 'text-status-success-fg' : 'text-status-failed-fg'}>
            {result.message}
          </Text>
        )}

        <div className="flex gap-2">
          <Button type="button" outline onClick={testConnection} disabled={testing}>
            {testing ? t('credentials.testing') : t('credentials.test_connection')}
          </Button>
          <Button type="submit" color="brand" disabled={form.processing}>
            {t('credentials.save')}
          </Button>
        </div>
      </form>
    </Card>
  )
}
